import React from 'react';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import { ArrowLeft, MapPin, Loader2, AlertTriangle } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useInspections } from '../hooks/api/inspections';

// Visual placeholders so cards still look rich while a project has no captured
// imagery yet.  Indexed by inspection-id hash for deterministic variation.
const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1693907795785-5cf4927a70fa?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1758609554326-ebf110a4c142?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1763428265772-f1f1b4a1231e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1744968777128-7dffe9ff46dc?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1715869428589-366729e7fdcd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1763104643676-38d8835d1f89?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
];

function placeholderFor(id = '') {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PLACEHOLDER_IMAGES[h % PLACEHOLDER_IMAGES.length];
}

export function ProjectSites({ projectId, projectTitle, onBack, onSiteClick }) {
  const { data, isLoading, isError, error } = useInspections({
    project_id: projectId, page: 1, page_size: 50,
  });
  const inspections = data?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <MapPin className="w-6 h-6 text-blue-500" />
          <div>
            <h2 className="text-2xl font-semibold">{projectTitle || 'Project'}</h2>
            <p className="text-sm text-muted-foreground">
              {inspections.length
                ? `${inspections.length} site${inspections.length === 1 ? '' : 's'} — select to view details`
                : 'No sites yet for this project'}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </Button>
      </div>

      {/* States */}
      {isLoading && (
        <div className="flex items-center gap-2 py-12 justify-center text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading sites…
        </div>
      )}
      {isError && (
        <div className="flex items-center gap-2 py-8 justify-center text-destructive text-sm">
          <AlertTriangle className="w-4 h-4" /> {error?.message || 'Failed to load sites'}
        </div>
      )}
      {!isLoading && !isError && inspections.length === 0 && (
        <Card className="border-border bg-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <p className="mb-2">No inspections yet for this project.</p>
            <p className="text-xs">Use Pilot Upload or Annotator Upload to add the first set of files.</p>
          </CardContent>
        </Card>
      )}

      {/* Site Cards Grid */}
      {!isLoading && inspections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {inspections.map((insp) => {
            const image = placeholderFor(insp.id);
            const location = insp.metadata?.location || '—';
            const progress = Math.max(0, Math.min(100, insp.progress || 0));
            return (
              <div
                key={insp.id}
                className="cursor-pointer group"
                onClick={() => onSiteClick?.(insp.id)}
              >
                <Card className="border-border bg-card hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden">
                  <CardContent className="p-0 relative h-64">
                    <ImageWithFallback
                      src={image}
                      alt={insp.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
                      <h4 className="font-semibold text-white text-lg line-clamp-2">{insp.name}</h4>
                      <div className="flex items-center text-sm text-white/90">
                        <MapPin className="w-4 h-4 mr-1" />
                        {location}
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs text-white/80">
                          <span>Progress</span>
                          <span>{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-1.5" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
